import {
  INTRANET_CHAT_MESSAGE_MAX,
  INTRANET_FILE_ACCEPT,
  INTRANET_IMAGE_ACCEPT,
  INTRANET_VIDEO_ACCEPT,
  intranetChatAttachmentPath,
  type IntranetChatAttachment,
} from '@gb-ticket/shared-types';
import {
  CameraIcon,
  FileIcon,
  ImageIcon,
  Loader2Icon,
  MapPinIcon,
  MicIcon,
  PlusIcon,
  SmileIcon,
  SquareIcon,
  VideoIcon,
} from 'lucide-react';
import { useRef, useState, type KeyboardEvent } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { API_BASE_URL } from '@/lib/api';
import { cn } from '@/lib/utils';

const MEDIA_ACCEPT = `${INTRANET_IMAGE_ACCEPT},${INTRANET_VIDEO_ACCEPT},.gif`;

const EMOJI = [
  '😀',
  '😁',
  '😂',
  '😊',
  '😍',
  '😎',
  '🤔',
  '😢',
  '👍',
  '👎',
  '👏',
  '🙏',
  '🎉',
  '❤️',
  '🔥',
  '✅',
] as const;

export function ChatAttachmentView({
  attachment,
  mine,
}: {
  attachment: IntranetChatAttachment;
  mine: boolean;
}) {
  if (attachment.scanStatus === 'PENDING') {
    return <p className="text-xs opacity-80">Wird geprüft …</p>;
  }
  if (attachment.scanStatus === 'BLOCKED') {
    return <p className="text-xs opacity-80">Datei blockiert.</p>;
  }
  const src = `${API_BASE_URL}${intranetChatAttachmentPath(attachment.id)}`;
  if (attachment.kind === 'image' || attachment.kind === 'gif') {
    return <img src={src} alt={attachment.filename} className="max-h-64 max-w-full rounded-lg" />;
  }
  if (attachment.kind === 'video') {
    return <video src={src} controls className="max-h-64 max-w-full rounded-lg" />;
  }
  if (attachment.kind === 'voice') {
    return <audio src={src} controls className="max-w-full" />;
  }
  return (
    <a href={src} className={cn('text-sm underline', mine ? 'text-white' : 'text-primary')}>
      {attachment.filename}
    </a>
  );
}

export function ChatComposer({
  draft,
  pending,
  reply,
  onDraft,
  onSendText,
  onSendFile,
  onCancelReply,
  onOpenLocation,
}: {
  draft: string;
  pending: boolean;
  reply?: { authorName: string; excerpt: string } | null;
  onDraft: (value: string) => void;
  onSendText: () => void;
  onSendFile: (file: File, voice?: boolean) => void;
  onCancelReply?: () => void;
  onOpenLocation?: () => void;
}) {
  const mediaRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const gifRef = useRef<HTMLInputElement>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  function pick(input: HTMLInputElement | null) {
    input?.click();
  }

  function onPicked(list: FileList | null) {
    const file = list?.[0];
    if (file) {
      onSendFile(file, false);
    }
  }

  async function toggleVoice() {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      setRecording(false);
      const blob = new Blob(chunksRef.current, { type: mime });
      if (blob.size > 0) {
        const extension = mime === 'audio/mp4' ? 'm4a' : 'webm';
        onSendFile(new File([blob], `sprachnachricht.${extension}`, { type: mime }), true);
      }
    };
    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSendText();
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSendText();
      }}
      className="border-t border-border bg-card px-3 py-2.5 sm:px-4 sm:py-3"
    >
      <input
        ref={mediaRef}
        type="file"
        accept={MEDIA_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-label="Bilder und Videos"
        onChange={(event) => {
          onPicked(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={photoRef}
        type="file"
        accept={INTRANET_IMAGE_ACCEPT}
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-label="Foto aufnehmen"
        onChange={(event) => {
          onPicked(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={videoRef}
        type="file"
        accept={INTRANET_VIDEO_ACCEPT}
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-label="Video aufnehmen"
        onChange={(event) => {
          onPicked(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={fileRef}
        type="file"
        accept={INTRANET_FILE_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-label="Datei"
        onChange={(event) => {
          onPicked(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={gifRef}
        type="file"
        accept="image/gif,.gif"
        className="sr-only"
        tabIndex={-1}
        aria-label="GIF"
        onChange={(event) => {
          onPicked(event.target.files);
          event.target.value = '';
        }}
      />

      <div className="flex flex-col gap-2">
        {reply ? (
          <p className="flex items-start justify-between gap-2 rounded-md bg-muted/70 px-2 py-1 text-xs">
            <span className="min-w-0">
              <span className="font-medium">{reply.authorName}</span>
              <span className="block truncate">{reply.excerpt}</span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={onCancelReply}
            >
              Abbrechen
            </Button>
          </p>
        ) : null}
        <Textarea
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          onKeyDown={onKeyDown}
          maxLength={INTRANET_CHAT_MESSAGE_MAX}
          placeholder="Schreib etwas …"
          aria-label="Nachricht"
          rows={2}
          className="max-h-40 min-h-11 w-full resize-none bg-background"
        />

        <div className="flex items-center gap-1 sm:gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-9 shrink-0"
                aria-label="Anhang"
                disabled={pending}
              >
                <PlusIcon aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onSelect={() => pick(photoRef.current)}>
                <CameraIcon aria-hidden />
                Foto aufnehmen
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => pick(videoRef.current)}>
                <VideoIcon aria-hidden />
                Video aufnehmen
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => pick(mediaRef.current)}>
                <ImageIcon aria-hidden />
                Bilder und Videos
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => pick(fileRef.current)}>
                <FileIcon aria-hidden />
                Datei
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onOpenLocation?.()}>
                <MapPinIcon aria-hidden />
                Standort
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 shrink-0 px-2 text-xs font-semibold tracking-wide"
            disabled={pending}
            onClick={() => pick(gifRef.current)}
          >
            GIF
          </Button>

          <div className="relative">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-9 shrink-0"
              aria-label="Emoji"
              aria-expanded={emojiOpen}
              disabled={pending}
              onClick={() => setEmojiOpen((open) => !open)}
            >
              <SmileIcon aria-hidden />
            </Button>
            {emojiOpen ? (
              <div className="absolute bottom-11 left-0 z-10 grid w-52 grid-cols-8 gap-1 rounded-md border bg-popover p-2 shadow-md sm:right-0 sm:left-auto">
                {EMOJI.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="rounded p-1 text-base hover:bg-muted"
                    onClick={() => {
                      onDraft(`${draft}${emoji}`);
                      setEmojiOpen(false);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <Button
            type="button"
            variant={recording ? 'default' : 'ghost'}
            size="icon"
            className="size-9 shrink-0"
            aria-label={recording ? 'Aufnahme beenden' : 'Sprachnachricht'}
            disabled={pending}
            onClick={() => void toggleVoice()}
          >
            {recording ? <SquareIcon aria-hidden /> : <MicIcon aria-hidden />}
          </Button>

          <div className="ml-auto min-w-0">
            <Button type="submit" disabled={!draft.trim() || pending} className="h-9 min-w-20 px-4">
              {pending ? <Loader2Icon className="size-4 animate-spin" aria-hidden /> : null}
              Senden
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
